import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CreateGalleryPresetBodyDto, GalleryPresetDto, UpdateGalleryPresetBodyDto } from '@picflow/types';
import { lastValueFrom } from 'rxjs';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';

@Injectable({
  providedIn: 'root',
})
export class GalleryPresetService {
  constructor(private http: HttpClient, private config: AppConfigService, private loader: DataLoaderService) {}

  async getGalleryPresets(): Promise<GalleryPresetDto[]> {
    const resp = await this.loader.get('gallery_presets', () => this.getGalleryPresetsInternal());
    const order = ['view-only', 'download', 'review', 'workflow'];

    const presets = [
      ...resp
        .filter(p => p.type === 'standard')
        .sort((a, b) => order.indexOf(a.name.toLowerCase()) - order.indexOf(b.name.toLowerCase())),
      ...resp.filter(p => p.type === 'custom').sort((a, b) => (a.created_at > b.created_at ? 1 : -1)),
    ];

    return presets;
  }

  async getGalleryPresetsInternal(): Promise<GalleryPresetDto[]> {
    const resp = await lastValueFrom(
      this.http.get<{ data: GalleryPresetDto[] }>(`${this.config.apiEndpoint}/gallery_presets`)
    );
    return resp.data;
  }

  async createPreset(preset: CreateGalleryPresetBodyDto) {
    const resp = await lastValueFrom(
      this.http.post<GalleryPresetDto>(`${this.config.apiEndpoint}/gallery_presets`, preset)
    );
    this.loader.remove('gallery_presets');
    return resp;
  }

  async deletePreset(id: string) {
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/gallery_presets/${id}`));
    this.loader.remove('gallery_presets');
  }

  async updatePreset(id: string, preset: UpdateGalleryPresetBodyDto) {
    await lastValueFrom(this.http.patch<GalleryPresetDto>(`${this.config.apiEndpoint}/gallery_presets/${id}`, preset));
    this.loader.remove('gallery_presets');
  }

  async makeDefault(id: string) {
    await this.updatePreset(id, { is_default: true });
  }
}
